import { Overlay } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { BaseMap } from 'src/app/modules/route-planner/components/shared/map/base-map.component';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import {
  MapService,
  MarkersClustering,
  MarkerTrackingOptions,
} from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { RoutePlannerAutoCompleteData } from '../../../source-destination-form/address-auto-complete/address-auto-complete.component';
import { CLICK_STATION_TO_SEE_DETAILS, PUBLIC_TRANSPORTATION_STATIONS, SEARCH_ADDRESS, STATION_CODE } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  map,
  tap,
  filter,
} from 'rxjs';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { MotAutocompleteComponent } from 'src/app/@forms/components/mot-autocomplete/mot-autocomplete.component';
import { MapComponent } from 'src/app/modules/route-planner/components/shared/map/map.component';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { eMapLocationType } from 'src/app/modules/route-planner/enums/route-planning/location-type.enum';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import { StationsAutoCompleteMapMarkerComponent } from './stations-auto-complete-map-marker/stations-auto-complete-map-marker.component';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';
import { StopDetails } from 'src/app/modules/route-planner/interfaces/location/stop-details.interface';
import { MapLocation } from 'src/app/modules/route-planner/interfaces/location/map-locations.interface';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';

@Component({
    selector: 'app-stations-auto-complete-map',
    templateUrl: './stations-auto-complete-map.component.html',
    styleUrl: './stations-auto-complete-map.component.scss',
    providers: [MapService],
    standalone: false
})
export class StationsAutoCompleteMapComponent
  extends BaseMap<Address> 
  implements OnInit, AfterViewInit {
  @ViewChild(MapComponent) override mapComponent?: MapComponent;
  @ViewChild('autocomplete') autoComplete: MotAutocompleteComponent<Address>;
  @ViewChild('mapContainer', { read: ViewContainerRef })
  mapContainerRef: ViewContainerRef;

  stationMarkersGroup: H.map.Group;

  activeStationMarker: H.map.Marker;
  stationDetailsMarker: H.map.DomMarker;
  stationCodeText: string;

  searchStationGroup: H.map.Group;

  /** Marker for the address selected from search (shows "where you are" on the map) */
  searchAddressMarker: H.map.Marker;

  clickStationToSeeDetailsText: string
  stops: import("src/app/modules/route-planner/interfaces/location/map-locations.interface").MapLocation<import("src/app/modules/route-planner/interfaces/location/stop-details.interface").StopDetails>[];
  
  ignoreMapTap = false; // Flag to prevent reopening after closing

  searchAddressText: string;
  constructor(
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,
    mapService: MapService,
    routePlanningService: RoutePlanningService,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: RoutePlannerAutoCompleteData,
    private readonly languageService: LanguageService,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly scheduleService: ScheduleService,
    private readonly userLocationService: UserLocationService,
    private readonly locationService: LocationService,

  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );
  }

  override ngOnInit(): void {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.stationCodeText = STATION_CODE[language.language];
      this.clickStationToSeeDetailsText = CLICK_STATION_TO_SEE_DETAILS[language.language]
      this.searchAddressText = SEARCH_ADDRESS[language.language];
    });
    
  }

  isInMobileView() {
    return this.resizeService.isMobile;
  }

  override ngAfterViewInit(): void {
    this.mapService.setViewContainerRef(this.mapContainerRef);
    this.results$ = this.autoComplete.textChange.pipe(
      tap(() => {
        this.mapService.removeObject(this.searchStationGroup);
        this.searchStationGroup = null;
        this.removeSearchAddressMarker();
      }),
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      distinctUntilChanged(),
      switchMap((val: string) => {
        if (!!val && val.length >= AutoCompleteService.AUTO_COMPLETE_MIN_CHAR) {
          return this.autoCompleteService.searchLocations(val);
        }

        return of([]);
      })
    );

    // Initialize map with focus on user location for initial view
    // But don't auto-center on location changes after user selects an address
    this._mapInitialized = this.mapService.initializeMap(
      this.mapComponent.mapDiv,
      {
        focusOnUserLocation: true, // Initial centering on user location
        controls: {
          zoom: true,
          myLocation: true,
        },
      }
    );
    
    // CRITICAL: add handler AFTER map initialization!
    this.mapService.$mapInitialized.subscribe((map) => {
      this._mapInitialized = true;
      
      map.addEventListener('tap', (ev) => {
        ev.preventDefault();
        
        // CRITICAL: don't handle clicks on markers - let their handlers fire!
        if (ev.target instanceof H.map.Marker || ev.target instanceof H.map.DomMarker) {
          return;
        }
        
        // Prevent reopening after closing with X button
        if (this.ignoreMapTap) {
          this.ignoreMapTap = false;
          return;
        }
        
        this.clearStationMarkersGroup();
      });
    });

    // this.userLocationService.$userLocationChanged
    //   .pipe(
    //     switchMap((userLocation) => {
    //       if (!!userLocation) {
    //         return this.scheduleService.getStopsCloseToLocation({
    //           lat: userLocation.location.lat,
    //           lng: userLocation.location.lng,
    //         });
    //       }
    //       return of(null);
    //     }),
    //     map((response) => response.data)
    //   ).subscribe((stops) => {
    //     this.mapService.addMarkersClustring(this.getMarkersClustering(stops))
    //     setTimeout(() => {
    //       this.mapService.updateMapView(this.userLocationService.userLocation.lat, this.userLocationService.userLocation.lng, 16)
    //     }, 500)
    //   })
    this.locationService.getMapStops().subscribe((response) => {
      const data = response.data;
      this.stops =  data.map((stop) => ({
        type: eMapLocationType.STATION,
        coordinates: { lat: stop.lat, lng: stop.lng },
        data: stop
      }));
      this.mapService.addMarkersClustring(this.getMarkersClustering(this.stops))

    })
  }
  getMarkersClustering(stops: MapLocation<StopDetails>[]): MarkersClustering {
    const options = {
      eps: 3,
      minWeight: 50,
    };


    const trackingOptions: MarkerTrackingOptions = {
      $location: this.mapService.$mapViewChanged.asObservable(),
      maxDistance: 500,
    };

    return {
      layers: [
      
        {
          dataPoints: this.stops.map((station) => ({
            ...station,
            type: eMapLocationType.STATION,
          })),
          dataPointObjectFactory: (noisePoint) => {
            return this.buildNoisePoint(noisePoint, MAP_ICONS.station());
          },
          options: options,
          tracking: trackingOptions,
        },
      ],
    };
  }

  private buildNoisePoint(
    noisePoint: H.clustering.INoisePoint,
    icon: H.map.Icon
  ): H.map.Marker {
    const locationData = noisePoint.getData();
    
    const marker = new H.map.Marker(noisePoint.getPosition(), {
      data: noisePoint.getData(),
      min: noisePoint.getMinZoom(),
      icon: icon,
    });

    // Add click event listener to show location name
    marker.addEventListener('tap', (ev) => {
      ev.stopPropagation();
      
      // locationData = { coordinates, data, type } - stopcode is inside data!
      const stopcode = locationData?.data?.stopcode || locationData?.stopcode;
      
      if (stopcode) {
        this.displayStationMarker(stopcode);
      }
    });

    return marker;
  }

  onStationSelected(address: Address) {
    if (!address.coordinates?.lat || !address.coordinates?.lng) {
      return;
    }

    this.removeSearchAddressMarker();
    const position = { lat: address.coordinates.lat, lng: address.coordinates.lng };
    this.searchAddressMarker = new H.map.Marker(position, {
      data: address,
      icon: MAP_ICONS.pin_green(),
    });
    this.mapService.addObject(this.searchAddressMarker);

    // Fly to selected address with animation
    this.mapService.updateMapView(address.coordinates.lat, address.coordinates.lng, 18, 0.1);
  }

  private removeSearchAddressMarker(): void {
    if (this.searchAddressMarker) {
      this.mapService.removeObject(this.searchAddressMarker);
      this.searchAddressMarker = null;
    }
  }

  displayStationMarker(stopCode: number) {
    // Prevent reopening after closing with X button
    if (this.ignoreMapTap) {
      this.ignoreMapTap = false;
      return;
    }
    
    this.formControl.disable();
    const stop = this.stops.find((stop) => stop.data.stopcode === stopCode);
    
    if (!stop) {
      this.formControl.enable();
      return;
    }
    
    // First remove the previous card
    this.clearStationMarkersGroup();

    const stopLocation = { lat: stop.coordinates.lat, lng: stop.coordinates.lng };
    this.activeStationMarker = new H.map.Marker(stopLocation, {
      data: stop,
      icon: MAP_ICONS.selected_station(),
    });

    const detailContainer = this.mapService.createMapObject<
      StationsAutoCompleteMapMarkerComponent,
      any
    >(StationsAutoCompleteMapMarkerComponent, { stop: stop.data as StopFullData, showPickStationButton: true })
    this.stationDetailsMarker = new H.map.DomMarker(stopLocation, {
      data: stop,
      icon: new H.map.DomIcon(detailContainer, {
        onAttach: (element) => {
          this.attachMarkerListeners(element, stop);
        }
      }),
    });

    this.searchStationGroup = new H.map.Group({
      data: {},
      objects: [this.activeStationMarker, this.stationDetailsMarker],
    });
    
    this.mapService.addObject(this.searchStationGroup);
    // Use 0.3 seconds for faster animation
    this.mapService.updateMapView(stop.coordinates.lat, stop.coordinates.lng, 18, 1);
    this.formControl.enable();

  }

  private attachMarkerListeners(element: Element, stop: MapLocation<StopDetails>): void {
    const bindListeners = () => {
      const button = element.getElementsByClassName('choose-station').item(0);
      if (button) {
        let handled = false;
        const handleChoose = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          if (handled) return;
          handled = true;

          this.clearStationMarkersGroup();

          const stopData: Stop = {
            stopid: 0,
            stopcode: stop.data.stopcode,
            stopSequence: 0,
            isDropoff: true,
            isPickup: true,
            name: stop.data.name[this.languageService.getSelectedLanguage().symbol] || stop.data.name.he || '',
            desc: stop.data.streetName || '',
            lat: stop.coordinates.lat,
            lng: stop.coordinates.lng,
            cityName: stop.data.cityName || '',
            streetName: stop.data.streetName || '',
            streetNumber: stop.data.streetNumber || '',
            specialMessages: [],
            nextStopTime: null as any,
          };

          this.closeCard(stopData as any);
        };

        button.addEventListener('touchend', handleChoose);
        button.addEventListener('click', handleChoose);
      } else {
        console.warn('Button not found in marker DOM');
      }

      const closeButton = element.getElementsByClassName('close-station-data').item(0);
      if (closeButton) {
        let closeHandled = false;
        const handleClose = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          if (closeHandled) return;
          closeHandled = true;
          this.clearStationMarkersGroup(true);
        };

        closeButton.addEventListener('touchend', handleClose);
        closeButton.addEventListener('click', handleClose);
      } else {
        console.warn('closeButton not found in marker DOM');
      }
    };

    const button = element.getElementsByClassName('choose-station').item(0);
    if (button) {
      bindListeners();
    } else {
      // On mobile, Angular may not have rendered the inner component yet.
      const observer = new MutationObserver(() => {
        if (element.getElementsByClassName('choose-station').item(0)) {
          observer.disconnect();
          bindListeners();
        }
      });
      observer.observe(element, { childList: true, subtree: true });
    }
  }

  private clearStationMarkersGroup(ignoreNextMapTap: boolean = false): void {
    if (this.searchStationGroup) {
      this.mapService.removeObject(this.searchStationGroup);
      this.searchStationGroup = null;
    }
    
    if (this.stationDetailsMarker) {
      this.mapService.removeObject(this.stationDetailsMarker);
      this.stationDetailsMarker = null;
    }
    
    if (this.activeStationMarker) {
      this.activeStationMarker = null;
    }

    if (ignoreNextMapTap) {
      this.ignoreMapTap = true;
    }
  }
}
